import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { HbtIcon } from '../HbtIcon';
import { HbtIconSize } from '../HbtIcon/types';
import styles from './styles.module.scss';
import { LargeFormFieldProps, LargeFormFieldStatus } from './types';
import './overrides.scss';

/**
 * @param {LargeFormFieldProps} props - list out the props.
 * @param {string} id - The unique identifier for the form field.
 * @param {string} [initialValue=''] - The initial value of the form field.
 * @param {string} [ariaText] - Accessible label text for assistive technologies.
 * @param {string} [helperText] - Helper text displayed below the input.
 * @param {string} [errorMessage] - Error message displayed when the input is invalid.
 * @param {boolean} [isReadOnly=false] - Specifies if the input is read-only.
 * @param {number} [maxLength] - Maximum allowed character length for the input.
 */

/**
 * Large Form Field component
 *
 * This is the Large Form field component written using storybook. It functions as a large text area input field.
 */

const MAX_CHARACTER_LENGTH = 1000;

export const LargeFormField: FC<LargeFormFieldProps> = memo(
  ({
    id,
    initialValue = '',
    ariaText,
    helperText,
    errorMessage,
    isReadOnly,
    maxLength,
    ...props
  }: LargeFormFieldProps) => {
    const [formFieldValue, setValue] = useState<string>(initialValue);
    const [formStatus, setFormStatus] = useState<LargeFormFieldStatus>('success');
    const [maxLengthValue, setMaxLengthValue] = useState(MAX_CHARACTER_LENGTH);
    const formErrorId = `error-textarea-${id}`;

    useEffect(() => {
      const valueString =
        typeof props.value === 'string' ? props.value : String(props.value ?? initialValue);
      setValue(valueString);
      setFormStatus(valueString.length > maxLengthValue ? 'error' : 'success');
      setMaxLengthValue(maxLength ?? MAX_CHARACTER_LENGTH);
    }, [props.value, initialValue, maxLength, maxLengthValue]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        const calculatedStatus: LargeFormFieldStatus =
          newValue.length > maxLengthValue ? 'error' : 'success';
        setValue(newValue);
        setFormStatus(calculatedStatus);

        if (props.onChange) {
          props.onChange(event);
        }
      },
      [maxLengthValue, props.onChange]
    );

    return (
      <>
        <Typography data-testid="core-form-field-label" className={styles.formLabel}>
          {props.label}
        </Typography>
        <div
          data-testid="core-form-field"
          className={`${styles.form} ${isReadOnly && styles.readOnly} ${
            formStatus === 'error' && styles.error
          } ${props.className || ''} ${styles.textareaWrapper}`.trim()}
        >
          <textarea
            {...props}
            id={id}
            value={formFieldValue}
            role="textbox"
            rows={props.rows || 5}
            readOnly={isReadOnly}
            aria-label={ariaText}
            aria-multiline="true"
            aria-invalid={formStatus === 'error' ? 'true' : 'false'}
            aria-errormessage={formErrorId}
            placeholder={props.placeholder}
            className={`${isReadOnly && styles.readOnly}`}
            onChange={handleChange}
          />
        </div>
        <Stack
          spacing={2}
          direction="row"
          justifyContent="space-between"
          data-testid="core-form-field-helper-container"
        >
          <Stack direction="row" className={styles.formHelperContainer}>
            {formStatus === 'error' && (
              <>
                <Box className={styles.formHelperIconContainer}>
                  <HbtIcon
                    size={HbtIconSize.SMALL}
                    type="icon_info_outlined"
                    className={`${styles.formHelperIcon} ${styles.formHelperError}`}
                  />
                </Box>
                <Box
                  role="alert"
                  id="error-message"
                  data-testid="core-form-field-error"
                  className={styles.formHelperError}
                >
                  {errorMessage ?? 'You’ve exceeded the maximum character count'}
                </Box>
              </>
            )}
            {helperText && formStatus !== 'error' && (
              <Box data-testid="core-form-field-helper" className={styles.formHelper}>
                {helperText}
              </Box>
            )}
          </Stack>
          <Box
            role="alert"
            id={formErrorId}
            data-testid="core-form-field-text-count"
            className={formStatus === 'error' ? styles.formHelperError : styles.formHelperTextCount}
          >
            {!!formFieldValue && maxLength && `${formFieldValue.length}/${maxLength}`}
          </Box>
        </Stack>
      </>
    );
  }
);
